import { Table } from "@/components/shared/ui/RegistryTable";
import { useGetCreditTransferListForRegistryPagination } from "@/hooks/registry/useGetCreditTransferListForRegistryPagination";
import { feRoutes } from "@/routes/feRoutes";
import type { CreditTransferListItem } from "@/types/creditTracking";
import { useMemo } from "react";
import { FullPageLoader } from "../shared/ui/Loader";
import PaginationTwo from "../shared/ui/PaginationTwo";

const formatTransferId = (id: number) => String(id).padStart(5, "0");
const formatTransferDate = (isoDate: string) => isoDate.slice(0, 10);

interface Props {
	hidePagination?: boolean;
}

const CreditTransferTableWrapper = ({ hidePagination = false }: Props) => {
	const { creditTransferList, pagination } = useGetCreditTransferListForRegistryPagination();
	const { data: transferListData, isLoading } = creditTransferList;
	const { changeOffset } = pagination;

	const list = transferListData?.data ?? [];

	const handleRowClick = (row: CreditTransferListItem) => {
		window.open(feRoutes.registry.transferDetails(row.creditTransferId), "_blank");
	};

	const columns = useMemo(
		() => [
			{
				header: "ID",
				cell: (row: { row: { original: CreditTransferListItem } }) => (
					<div className="min-w-[60px]">{formatTransferId(row.row.original.creditTransferId)}</div>
				),
				accessorKey: "creditTransferId",
			},
			{
				header: "Transfer Date",
				cell: (row: { row: { original: CreditTransferListItem } }) => (
					<div className="min-w-[110px]">{formatTransferDate(row.row.original.eventDate)}</div>
				),
				accessorKey: "eventDate",
			},
			{
				header: "Project",
				cell: (row: { row: { original: CreditTransferListItem } }) => (
					<div className="min-w-[180px] whitespace-normal break-words">{row.row.original.projectTitle}</div>
				),
				accessorKey: "projectTitle",
			},
			{
				header: "Sender",
				cell: (row: { row: { original: CreditTransferListItem } }) => (
					<div className="min-w-[140px] max-w-[280px] whitespace-normal break-words">
						{row.row.original.sender.legalName}
					</div>
				),
				accessorKey: "sender",
			},
			{
				header: "Recipient",
				cell: (row: { row: { original: CreditTransferListItem } }) => (
					<div className="min-w-[140px] max-w-[280px] whitespace-normal break-words">
						{row.row.original.recipient.legalName}
					</div>
				),
				accessorKey: "recipient",
			},
			{
				header: () => <div className="w-full text-right">Credits</div>,
				cell: (row: { row: { original: CreditTransferListItem } }) => (
					<div className="min-w-[80px] text-right">{row.row.original.totalCreditCount.toLocaleString("en-US")}</div>
				),
				accessorKey: "totalCreditCount",
			},
		],
		[],
	);

	if (isLoading) return <FullPageLoader />;

	return (
		<div>
			{(!list || list.length === 0) && (
				<div className="flex justify-center py-20">
					<p>No transfers yet.</p>
				</div>
			)}

			{list && list.length > 0 && (
				<div>
					{!hidePagination && transferListData && transferListData.count > list.length && (
						<div className="mt-8 flex w-full justify-end py-0 pb-0 pr-0 md:px-8">
							<PaginationTwo
								count={transferListData.count}
								changeOffset={changeOffset}
								offset={parseInt(transferListData.offset, 10)}
								limit={parseInt(transferListData.limit, 10)}
							/>
						</div>
					)}
					<Table data={list} columns={columns} onRowClick={handleRowClick} />
				</div>
			)}
		</div>
	);
};

export default CreditTransferTableWrapper;
